import { FilterCompTypes } from 'app/ComponentLibrary/Filter/utils';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { initialCandidateAggregateSearch } from 'app/ComponentLibrary/Filter/CandidateSearch';
import { initialCandidateSkillSetFilter } from 'app/ComponentLibrary/Filter/CandidateSkillset';
import { CustomFilter } from 'app/ComponentLibrary/Filter';
import { Grid, Collapse } from '@mui/material';
import { candidateSearchFilterName } from 'app/components/GlobalSearch/Candidate/SearchUtils';
import { getMultiSelectOptions } from 'app/components/GlobalSearch/helper';
import { getTagsOpts } from 'app/components/GlobalSearch/Candidate/FilterUtils';
import { totalCountNodes } from 'app/components/Common/TreeView/TreeViewHelpers';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import { selectGridFiltersExpanded } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { defaultFilterName, SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { getSearchFilterOptions, getSearchLookups } from 'app/services/SharedServices/SharedServices';
import tagsData from 'app/assets/jsons/Tags.json';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'types';
import { removeSessionValue, SessionKey, StorageType } from 'utils/customHooks/sessionStorage/sessionHelpers';

type FilterOption = { id: string | number; name: string; value: string | number };

interface Props {
  defaultFilterValues: any;
  gridTag: string;
}

export const TagsCandidateSearchFilters = ({ defaultFilterValues, gridTag }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset, setValue } = useFormContext();
  const filtersExpanded = useSelector(selectGridFiltersExpanded);
  const filterPreferences = useSelector((state: RootState) =>
    state.userPreferenceData.userGridPreferences.find(item => item.id === gridTag),
  );

  const [brandOpts, setBrandOpts] = useState<FilterOption[]>([]);
  const [candidateStatusOpts, setCandidateStatusOpts] = useState<FilterOption[]>([]);
  const [tagsOpts, setTagsOpts] = useState<FilterOption[]>([]);

  const fields = useMemo(
    () => [
      {
        filterName: t('search.globalSearch.candidate.filterNames.recruiter'),
        name: candidateSearchFilterName.recruiter,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        applyOnClickAway: true,
        isMultiSelect: true,
        fetchOptions: async key => await getSearchLookups('recruiters', key),
        isSelectAll: true,
        applyOnEnter: true,
      },
      {
        filterName: t('search.globalSearch.candidate.filterNames.candidate'),
        name: candidateSearchFilterName.candidateAgggreggate,
        type: FilterCompTypes.CANDIDATESEARCH,
        applyOnClickAway: true,
        isMultiSelect: true,
      },
      {
        filterName: t('search.globalSearch.candidate.filterNames.brand'),
        name: candidateSearchFilterName.brand,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        applyOnClickAway: true,
        isMultiSelect: true,
        fetchOptions: async key => {
          if (!key) return brandOpts;
          const low = key.toLowerCase();
          return brandOpts.filter(opt => opt.name?.toLowerCase().includes(low) || `${opt.value}` === key);
        },
        isSelectAll: true,
        applyOnEnter: true,
      },
      {
        filterName: t('search.globalSearch.candidate.filterNames.candidStatus'),
        name: candidateSearchFilterName.candidateStatus,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        applyOnClickAway: true,
        isMultiSelect: true,
        fetchOptions: async key => {
          if (!key) return candidateStatusOpts;
          const low = key.toLowerCase();
          return candidateStatusOpts.filter(opt => opt.name?.toLowerCase().includes(low) || `${opt.value}` === key);
        },
        isSelectAll: true,
        applyOnEnter: true,
      },
      {
        filterName: t('search.globalSearch.candidate.filterNames.skillSet'),
        name: candidateSearchFilterName.candidateSkillSet,
        type: FilterCompTypes.CANDIDATESKILLSET,
        isMultiSelect: true,
        options: [],
        placeholder: 'Select',
        selectAllValue: 'All',
        version2: true,
        returnsObjectAsValue: true,
        size: 'large',
        customSelectedCount: value => totalCountNodes(value),
      },
      {
        filterName: t('search.globalSearch.candidate.filterNames.tags'),
        name: candidateSearchFilterName.candidateTags,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        applyOnClickAway: true,
        isMultiSelect: true,
        fetchOptions: async key => {
          if (!key) return tagsOpts;
          const low = key.toLowerCase();
          return tagsOpts.filter(opt => opt.name?.toLowerCase().includes(low) || `${opt.value}` === key);
        },
        isSelectAll: true,
        applyOnEnter: true,
      },
    ],
    [brandOpts, candidateStatusOpts, t, tagsOpts],
  );

  useEffect(() => {
    const fetchOptions = async () => {
      const [brands, statuses] = await Promise.all([
        getSearchFilterOptions('brands', true),
        getSearchFilterOptions('candidatestatuses', true),
      ]);
      setBrandOpts(getMultiSelectOptions(brands || [], { withoutAllOption: true }) as FilterOption[]);
      setCandidateStatusOpts(getMultiSelectOptions(statuses || [], { withoutAllOption: true }) as FilterOption[]);
      setTagsOpts(getTagsOpts(tagsData || []) as FilterOption[]);
    };
    trackPromise(fetchOptions(), 'gloabl-search-call');
  }, []);

  const resetFilter = () => {
    const foundDefaultIndex = (filterPreferences?.value?.filterAttributes?.filterSet || [])?.find(
      item => item.name === defaultFilterName || item.isDefault,
    );
    const filterPrefClone = JSON.parse(JSON.stringify(filterPreferences));
    if (filterPrefClone?.value) {
      filterPrefClone.value.selectedFilter = foundDefaultIndex || null;
      removeSessionValue(SessionKey[gridTag], StorageType.sessionStorage);
      dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(filterPrefClone));
    }
    setValue(candidateSearchFilterName.candidateTags, []);
    setValue(candidateSearchFilterName.candidateSkillSet, initialCandidateSkillSetFilter);
    setValue(candidateSearchFilterName.candidateAgggreggate, initialCandidateAggregateSearch);
    reset(foundDefaultIndex ? Object.fromEntries(foundDefaultIndex.value) : defaultFilterValues);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={11}>
        <Collapse in={filtersExpanded} timeout={200} collapsedSize={45}>
          <CustomFilter
            fields={fields}
            justifyContent={'flex-start'}
            version2
            resetBtn
            analytics={{
              caller: t('search.globalSearch.globalSearchFilter'),
              onApply: true,
              properties: { category: SearchType.tagsCandidateSearch },
            }}
          />
        </Collapse>
      </Grid>
      <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end', mt: '3px' }}>
        <SearchPageIcons
          resetlbutton
          funnelButton
          checked={filtersExpanded}
          onResetForm={resetFilter}
          onAdvancedFilterClick={() => dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded))}
        />
      </Grid>
    </Grid>
  );
};

